import { create } from 'zustand';
import {
  collection,
  query,
  orderBy,
  limit,
  doc,
  setDoc,
  deleteDoc,
  writeBatch,
} from '@/lib/firebase/firestore';
import { getDocsFromServer } from 'firebase/firestore';
import {
  ref,
  onValue,
  set as rtdbSet,
  get as rtdbGet,
  increment as rtdbIncrement,
  off,
  type DatabaseReference,
} from 'firebase/database';
import { db, rtdb } from '@/config/firebase';
import { requestFCMToken, onFCMMessage } from '@/lib/firebase/messaging';
import { callFunction } from '@/lib/firebase/functions';
import { setStorageItem, getStorageItem } from '@/lib/utils/storage';
import { decodeConvIdFromHash } from '@/lib/utils/format';
import { openConversationInInbox } from '@/stores/conversation-store';
import { useUIStore } from '@/stores/ui-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { emitIncomingPreview } from '@/components/common/incoming-message-preview';
import { readScopedCache } from '@/lib/cache/scoped-cache';
import { isTPAReauthWipePending } from '@/modules/tpa/tpa-reauth-cleanup';

export interface SubscribedDevice {
  token: string;
  userAgent?: string;
  addedTime?: number;
  regContext?: string;
}

// Single-flight for initializeFCM: concurrent callers join the running chain
// instead of starting a second one (or being turned away mid-flight).
let inflightInitializeFCM: Promise<void> | null = null;

// Unsubscribe for the current foreground onMessage listener. onMessage stacks
// a NEW observer on every call, so we detach the previous one before attaching
// again — otherwise each reattach (page load, impersonation switch) would add
// another handler and fire duplicate toasts for the same message.
let foregroundUnsub: (() => void) | null = null;

// appConfig is populated asynchronously by use-app-init, so early callers can
// see orgId as undefined. Registering a token with org_id missing makes the
// server key the subscription under a separate "null-…" doc that the org
// mirror never sees (ghost devices the org-side view can't account for), so
// fall back to the cached org id like the tpa modules do.
function getEffectiveOrgId(): string | number | null {
  return (
    useAppStore.getState().appConfig?.orgId ||
    readScopedCache('ua_cache_appOrgId') ||
    null
  );
}

// Where this registration is happening: a plain browser tab, or embedded as
// an iframe inside a host SaaS (e.g. Zoho CRM). Stored with the token so the
// device list can show where each subscription came from.
// Format: "<tab|iframe[:saasHost]>@<appHost>" — the suffix records which
// deployment of the app the user enabled from (sms.ulgebra.com,
// app.ulgebra.com, localhost:3000, …). Older tokens hold the bare prefix.
function getRegistrationContext(): string {
  const appHost = window.location.host;
  const embedded = window.self !== window.top;
  if (!embedded) return `tab@${appHost}`;
  try {
    const params = new URLSearchParams(window.location.search);
    const host = params.get('serviceOrigin') || params.get('frameorigin') || document.referrer;
    if (host) return `iframe:${new URL(host).hostname}@${appHost}`;
  } catch {
    // fall through
  }
  return `iframe@${appHost}`;
}

export interface NotificationItem {
  id: string;
  nTtl: string;       // title, e.g. "Lead from' Twilio - 919994411345"
  nMsg: string;        // message body preview
  ct: number;          // created timestamp (ms)
  readTime?: number;   // set when user marks as read
  nGrpID?: string;     // conversation / group ID for opening chat
  app?: string;        // app code
}

interface NotificationStore {
  // FCM
  fcmToken: string | null;
  enabled: boolean;
  loading: boolean;

  // Server-derived subscription state (source of truth for the settings toggle).
  // localStorage is partitioned between top-level tabs and third-party iframes
  // (e.g. embedded inside Zoho CRM), so the locally cached token can't be
  // trusted to reflect whether this user is actually subscribed — only the
  // server list at ulgebraUsers/{uid}/notification_subscriptions is shared
  // across contexts. null = not yet fetched.
  serverSubscribed: boolean | null;
  subscribedTokens: string[];
  subscribedDevices: SubscribedDevice[];
  // True while the page-load reattach chain (status fetch → token
  // register/rotate) is running — the settings UI shows a loading state
  // instead of flashing a transient Disabled/Enabled.
  statusPending: boolean;
  // True after a status fetch actually threw (network/permission error). This
  // is a TERMINAL state distinct from serverSubscribed === null: reattachFCM
  // treats null as "don't guess", so the failure can't be encoded there — the
  // settings UI reads statusError to stop the spinner and offer a retry.
  statusError: boolean;

  // Notifications panel
  alertCount: number;
  notifications: NotificationItem[];
  panelOpen: boolean;
  notificationsLoading: boolean;

  // RTDB listener ref (for cleanup)
  _countRef: DatabaseReference | null;

  // FCM actions
  initializeFCM(): Promise<void>;
  reattachFCM(): Promise<void>;
  requestPermission(): Promise<boolean>;
  unsubscribeDevice(): Promise<void>;
  unsubscribeToken(token: string): Promise<void>;
  fetchSubscriptionStatus(): Promise<void>;

  // Alert count (mirrored from RTDB — do not mutate directly on FCM events)
  setAlertCount(count: number): void;
  clearAlerts(): void;

  // RTDB real-time unread count listener
  startListeningUnreadCount(userId: string, extensionName: string): void;
  stopListeningUnreadCount(): void;
  /** One-shot re-read of the unread count (e.g. after the re-auth mask is lifted). */
  refreshUnreadCount(): Promise<void>;

  // Notification list actions
  togglePanel(): void;
  closePanel(): void;
  fetchNotifications(userId: string, extensionName: string): Promise<void>;
  markAsRead(userId: string, extensionName: string, notifId: string): Promise<void>;
  markAllAsRead(userId: string, extensionName: string): Promise<void>;
  removeNotification(userId: string, extensionName: string, notifId: string): Promise<void>;
  /** KAN-188: delete every notification for this user + extension (not only the loaded page). */
  clearAllNotifications(userId: string, extensionName: string): Promise<void>;
}

// Foreground FCM handler — shows the desktop notification + in-app preview
// while a tab is open (the service worker covers background). Attached on
// enable and re-attached on every page load.
async function attachForegroundMessageHandler() {
  // Detach the previous listener first so repeated attaches don't stack
  // observers (which would fire the notification/preview twice per message).
  if (foregroundUnsub) {
    try { foregroundUnsub(); } catch { /* noop */ }
    foregroundUnsub = null;
  }
  foregroundUnsub = await onFCMMessage((payload) => {
    console.log('FCM message received:', payload);
    // NOTE: don't call incrementAlertCount() here. The unread count is
    // mirrored in RTDB at `user_unread_notifications_count/{uid}/{ext}`
    // and `startListeningUnreadCount` keeps `alertCount` in sync with
    // that value. Incrementing locally on FCM caused a transient
    // double-count (RTDB bumps to N+1, then the local ++ pushes it to
    // N+2) that only self-corrected on the next RTDB emit.

    // Extract title/body from notification or data payload (backend may send either)
    const title = payload.notification?.title || payload.data?.title || 'New message';
    const body = payload.notification?.body || payload.data?.body || 'You have a new message';
    let linkConvId: string | null = null;
    try {
      const link = payload.fcmOptions?.link;
      if (link) {
        const token = new URL(link).hash.replace(/^#/, '').split(/[?&/]/)[0];
        linkConvId = decodeConvIdFromHash(decodeURIComponent(token));
      }
    } catch {
      linkConvId = null;
    }
    const conversationId = payload.data?.nGrpID || payload.data?.conversationId || linkConvId || undefined;

    // Show desktop notification (foreground — service worker handles background)
    try {
      const n = new Notification(title, {
        body,
        icon: '/images/ulgebra-icon.png',
        tag: conversationId || 'ulgebra-notification',
      });
      // Clicking the notification focuses the window and opens the chat
      // dialog for that conversation (when the payload carries an id).
      n.onclick = (evt) => {
        evt.preventDefault();
        try { window.focus(); } catch { /* noop */ }
        if (conversationId) {
          void openConversationInInbox(conversationId);
        }
        n.close();
      };
    } catch (e) {
      console.warn('[Notifications] Desktop notification failed:', e);
    }

    // Show in-app floating preview if user is not on inbox/chat
    const currentView = useUIStore.getState().activeView;
    const showPreview = useSettingsStore.getState().preferences.showIncomingPreview ?? true;
    if (showPreview && currentView !== 'CONVERSATIONS' && currentView !== 'CHAT') {
      emitIncomingPreview({
        title,
        body,
        conversationId,
        timestamp: Date.now(),
      });
    }
  });
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  fcmToken: getStorageItem('fcm_token', null),
  alertCount: 0,
  enabled: getStorageItem('notification_permission', false),
  loading: false,
  serverSubscribed: null,
  subscribedTokens: [],
  subscribedDevices: [],
  statusPending: false,
  statusError: false,
  notifications: [],
  panelOpen: false,
  notificationsLoading: false,
  _countRef: null,

  /* ───────────── FCM ───────────── */

  fetchSubscriptionStatus: async () => {
    const uid = useAuthStore.getState().user?.uid;
    const extensionName = useAppStore.getState().extensionName;
    // Auth not ready yet — not an error, just too early. The settings effect
    // re-runs this once uid/extensionName load, so leave state untouched.
    if (!uid || !extensionName) return;
    const orgId = getEffectiveOrgId();
    const { selectedAdminUserId } = useAuthStore.getState();
    set({ statusError: false });
    try {
      const colRef = collection(db, 'ulgebraUsers', uid, 'notification_subscriptions');
      // Bypass IndexedDB cache — the whole point is cross-context truth
      const snap = await getDocsFromServer(query(colRef, limit(20)));
      const devices: SubscribedDevice[] = [];
      snap.docs.forEach((d) => {
        const sub = d.data() as {
          appCode?: string;
          orgId?: string | number;
          inviter?: { uid?: string };
          deviceRegistrationToken?: Array<
            | {
                deviceRegistrationToken?: string;
                userAgent?: string;
                addedTime?: number;
                regContext?: string;
              }
            | string
          >;
        };
        if (sub.appCode !== extensionName) return;
        if (orgId != null && sub.orgId != null && String(sub.orgId) !== String(orgId)) return;
        // Shared users: only the subscription invited by the selected admin counts
        if (selectedAdminUserId && sub.inviter?.uid !== selectedAdminUserId) return;
        (sub.deviceRegistrationToken || []).forEach((item) => {
          if (typeof item === 'string') {
            devices.push({ token: item });
          } else if (item?.deviceRegistrationToken) {
            devices.push({
              token: item.deviceRegistrationToken,
              userAgent: item.userAgent,
              addedTime: item.addedTime,
              regContext: item.regContext,
            });
          }
        });
      });
      set({
        serverSubscribed: devices.length > 0,
        subscribedTokens: devices.map((d) => d.token),
        subscribedDevices: devices,
      });
    } catch (err) {
      console.error('[Notifications] Failed to fetch subscription status:', err);
      // Terminal failure — stop the settings spinner and surface a retry.
      // serverSubscribed stays null so reattachFCM still refuses to guess.
      set({ statusError: true });
    }
  },

  // Page-load path: RE-ATTACH the FCM listener only if the server still has
  // this browser's token. Never enrolls — a token removed remotely (from the
  // device list on another machine) must stay removed; re-registering it here
  // was the bug where a disabled device re-enabled itself on reload. Only an
  // explicit Enable click may add a token.
  reattachFCM: async () => {
    const { enabled, loading, fcmToken, statusPending } = get();
    // statusPending doubles as a re-entrancy guard — StrictMode double-mounts
    // the effect that calls this, and two concurrent reattach chains raced
    // each other into registering two different tokens (duplicate devices).
    if (!enabled || loading || statusPending || !fcmToken) return;
    set({ statusPending: true });
    try {
      await get().fetchSubscriptionStatus();
      if (get().serverSubscribed === null) {
        // Status fetch failed — we can't safely make server writes (token
        // rotate, or clearing a remotely-unsubscribed token), so don't guess.
        // But re-attaching the foreground listener is read-only and local: the
        // token we already hold is presumed valid, and if it was actually
        // disabled remotely the server simply won't push to it. Skipping this
        // silently kills in-tab toasts for the whole session, so attach it and
        // bail out of the write paths only.
        await attachForegroundMessageHandler();
        return;
      }
      if (get().subscribedTokens.includes(fcmToken)) {
        // Fast path: if the browser still holds the same token, only the
        // foreground listener needs re-attaching — no server writes at all.
        const currentToken = await requestFCMToken();
        if (currentToken === fcmToken) {
          await attachForegroundMessageHandler();
          return;
        }
        // Token rotated — full re-register (adds new, removes stale)
        await get().initializeFCM();
      } else {
        console.log('[Notifications] Token was unsubscribed remotely — clearing local state');
        set({ fcmToken: null, enabled: false });
        setStorageItem('fcm_token', null);
        setStorageItem('notification_permission', false);
      }
    } finally {
      set({ statusPending: false });
    }
  },

  initializeFCM: () => {
    // Join, not skip: a second caller (Enable clicked while the page-load
    // rotate chain runs, or vice versa) awaits the SAME in-flight run and
    // only observes its settled result. The previous boolean guard returned
    // immediately, so callers read half-done state — requestPermission
    // reported success/failure from the OTHER chain's leftovers, and
    // reattachFCM could wipe state mid-registration.
    inflightInitializeFCM ??= (async () => {
      set({ loading: true });
      // FCM can rotate the token (returns a fresh one for the same browser,
      // frequent on dev reloads). Remember the previous token so the stale
      // registration can be removed — otherwise every rotation adds a ghost
      // "device" on the server.
      const previousToken = get().fcmToken;
      try {
        const token = await requestFCMToken();
        if (!token) return;
        // fcmToken/enabled flip now so the UI reflects progress, but
        // `loading` stays true until the SERVER acknowledges the token —
        // enabling isn't done at the local step (the chain rolls back on
        // server failure), and releasing the flag here opened a window
        // where reattachFCM read the server list before registration
        // landed and wiped state as "unsubscribed remotely".
        set({ fcmToken: token, enabled: true });
        setStorageItem('fcm_token', token);
        setStorageItem('notification_permission', true);

        // Register token with backend (mirrors old app's sendTokenToServer).
        // If this fails, clear token so the UI doesn't show "enabled" when the
        // server doesn't know about this device — local notifications won't work
        // without server-side registration.
        try {
          const extensionName = useAppStore.getState().extensionName;
          const orgId = getEffectiveOrgId();
          const { selectedAdminUserId } = useAuthStore.getState();
          await callFunction('processUserNofiticationToken', {
            extensionName,
            appCode: extensionName,
            org_id: orgId,
            deviceRegistrationToken: token,
            action: 'accept_token',
            registrationContext: getRegistrationContext(),
            _ua_lic_adminUserID: selectedAdminUserId,
          });
          console.log('[Notifications] Token registered with server');
          set({
            serverSubscribed: true,
            subscribedTokens: [...new Set([...get().subscribedTokens, token])],
          });
          // Token rotated — delete the stale registration so this browser
          // keeps exactly one entry on the server
          if (previousToken && previousToken !== token) {
            console.log('[Notifications] Token rotated — removing stale registration');
            try {
              await callFunction('processUserNofiticationToken', {
                extensionName,
                appCode: extensionName,
                org_id: orgId,
                deviceRegistrationToken: previousToken,
                action: 'delete_device_subscription',
                _ua_lic_adminUserID: selectedAdminUserId,
              });
            } catch (err) {
              console.warn('[Notifications] Failed to remove stale token:', err);
            }
          }
          // Refresh device list so the new registration shows with its
          // userAgent/addedTime details. Awaited — returning before the list
          // reflects the new token made the toggle flash "Disabled" briefly.
          await get().fetchSubscriptionStatus();
        } catch (err) {
          console.warn('[Notifications] Failed to register token with server:', err);
          // Server doesn't know about this device — treat as failure
          set({ fcmToken: null, enabled: false });
          setStorageItem('fcm_token', null);
          setStorageItem('notification_permission', false);
          return;
        }

        await attachForegroundMessageHandler();
      } catch (error) {
        console.warn('FCM initialization failed:', error);
        set({ fcmToken: null, enabled: false });
        setStorageItem('fcm_token', null);
        setStorageItem('notification_permission', false);
      } finally {
        // Busy for the WHOLE chain (token + server registration + refresh),
        // success or failure — this is what closes the reattach window.
        set({ loading: false });
      }
    })().finally(() => {
      inflightInitializeFCM = null;
    });
    return inflightInitializeFCM;
  },

  requestPermission: async () => {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      set({ enabled: false });
      setStorageItem('notification_permission', false);
      return false;
    }
    await get().initializeFCM();
    // Only report success if token was actually obtained and registered
    const success = !!get().fcmToken;
    if (!success) {
      set({ enabled: false });
      setStorageItem('notification_permission', false);
    }
    return success;
  },

  unsubscribeDevice: async () => {
    // Remove only THIS browser's registration — other devices are managed
    // individually from the device list in settings.
    const localToken = get().fcmToken;
    if (localToken) {
      await get().unsubscribeToken(localToken);
      return;
    }
    // Nothing registered from this browser — just clear stale local flags
    set({ fcmToken: null, enabled: false });
    setStorageItem('fcm_token', null);
    setStorageItem('notification_permission', false);
    await get().fetchSubscriptionStatus();
  },

  unsubscribeToken: async (token) => {
    const extensionName = useAppStore.getState().extensionName;
    const orgId = getEffectiveOrgId();
    const { selectedAdminUserId } = useAuthStore.getState();
    try {
      await callFunction('processUserNofiticationToken', {
        extensionName,
        appCode: extensionName,
        org_id: orgId,
        deviceRegistrationToken: token,
        action: 'delete_device_subscription',
        _ua_lic_adminUserID: selectedAdminUserId,
      });
      console.log('[Notifications] Token unsubscribed from server');
    } catch (err) {
      console.error('[Notifications] Failed to unsubscribe token:', err);
    }
    // If this browser's own token was removed, clear the local cache too so
    // the auto re-subscribe on reload doesn't resurrect it
    if (token === get().fcmToken) {
      set({ fcmToken: null, enabled: false });
      setStorageItem('fcm_token', null);
      setStorageItem('notification_permission', false);
    }
    await get().fetchSubscriptionStatus();
  },

  /* ───────────── Alert count ───────────── */

  setAlertCount: (count) => set({ alertCount: count }),

  clearAlerts: () => set({ alertCount: 0 }),

  /* ───────────── RTDB unread count listener ───────────── */

  startListeningUnreadCount: (userId, extensionName) => {
    const existing = get()._countRef;
    if (existing) off(existing);

    const path = `user_unread_notifications_count/${userId}/${extensionName}`;
    console.log('[NotificationStore] Listening unread count at:', path);
    const countRef = ref(rtdb, path);
    set({ _countRef: countRef });

    onValue(countRef, (snapshot) => {
      let count = snapshot.val() && !isTPAReauthWipePending(extensionName) ? Math.ceil(snapshot.val() * 10) / 10 : 0;
      if (count < 0) count = 0;
      set({ alertCount: count });
    });
  },

  stopListeningUnreadCount: () => {
    const countRef = get()._countRef;
    if (countRef) {
      off(countRef);
      set({ _countRef: null });
    }
  },

  refreshUnreadCount: async () => {
    // KAN-135: onValue only fires on a change. When a same-account re-auth
    // is 'skipped' the counter never changes, so the masked 0 would stick
    // until the next notification — re-read it once instead.
    const countRef = get()._countRef;
    if (!countRef) return;
    try {
      const snapshot = await rtdbGet(countRef);
      let count = snapshot.val() ? Math.ceil(snapshot.val() * 10) / 10 : 0;
      if (count < 0) count = 0;
      set({ alertCount: count });
    } catch { /* best-effort */ }
  },

  /* ───────────── Panel ───────────── */

  togglePanel: () => set({ panelOpen: !get().panelOpen }),
  closePanel: () => set({ panelOpen: false }),

  /* ───────────── Fetch notifications from Firestore ───────────── */

  fetchNotifications: async (userId, extensionName) => {
    // KAN-135: while the old account's history is still being wiped, show
    // nothing rather than the old items; the next open after the wipe
    // fetches the real (empty) list.
    if (isTPAReauthWipePending(extensionName)) {
      set({ notifications: [], notificationsLoading: false });
      return;
    }
    set({ notificationsLoading: true });
    console.log('[NotificationStore] Fetching notifications for:', userId, extensionName);
    try {
      const colRef = collection(
        db,
        'ulgebraUsers',
        userId,
        'extensions',
        extensionName,
        'notifications'
      );
      const q = query(colRef, orderBy('ct', 'desc'), limit(20));
      // Always bypass IndexedDB cache — opening the panel should reflect the
      // latest server state (new notifications, read/delete ops from other
      // devices). The default cache-first wrapper would resolve with stale
      // data and only refresh in the background.
      const snap = await getDocsFromServer(q);
      console.log('[NotificationStore] Fetched', snap.docs.length, 'notifications (from server)');
      const items: NotificationItem[] = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as NotificationItem[];
      set({ notifications: items, notificationsLoading: false });
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      set({ notificationsLoading: false });
    }
  },

  /* ───────────── Mark single as read ───────────── */

  markAsRead: async (userId, extensionName, notifId) => {
    try {
      const notifDoc = doc(
        db,
        'ulgebraUsers',
        userId,
        'extensions',
        extensionName,
        'notifications',
        notifId
      );
      await setDoc(notifDoc, { readTime: Date.now() }, { merge: true });

      // Update local state
      set({
        notifications: get().notifications.map((n) =>
          n.id === notifId ? { ...n, readTime: Date.now() } : n
        ),
      });

      // Decrement RTDB count
      if (get().alertCount > 0) {
        const countRef = ref(rtdb, `user_unread_notifications_count/${userId}/${extensionName}`);
        await rtdbSet(countRef, rtdbIncrement(-1));
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  },

  /* ───────────── Mark all as read ───────────── */

  markAllAsRead: async (userId, extensionName) => {
    try {
      // Set RTDB count to 0
      const countRef = ref(rtdb, `user_unread_notifications_count/${userId}/${extensionName}`);
      await rtdbSet(countRef, 0);

      // Mark each unread notification in Firestore
      const unread = get().notifications.filter((n) => !n.readTime);
      const now = Date.now();
      await Promise.all(
        unread.map((n) => {
          const notifDoc = doc(
            db,
            'ulgebraUsers',
            userId,
            'extensions',
            extensionName,
            'notifications',
            n.id
          );
          return setDoc(notifDoc, { readTime: now }, { merge: true });
        })
      );

      // Update local state
      set({
        notifications: get().notifications.map((n) =>
          n.readTime ? n : { ...n, readTime: now }
        ),
      });
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  },

  /* ───────────── Remove notification ───────────── */

  removeNotification: async (userId, extensionName, notifId) => {
    try {
      const notif = get().notifications.find((n) => n.id === notifId);
      const isUnread = notif && !notif.readTime;

      const notifDoc = doc(
        db,
        'ulgebraUsers',
        userId,
        'extensions',
        extensionName,
        'notifications',
        notifId
      );
      await deleteDoc(notifDoc);

      // Remove from local state
      set({
        notifications: get().notifications.filter((n) => n.id !== notifId),
      });

      // Decrement RTDB count if was unread
      if (isUnread && get().alertCount > 0) {
        const countRef = ref(rtdb, `user_unread_notifications_count/${userId}/${extensionName}`);
        await rtdbSet(countRef, rtdbIncrement(-1));
      }
    } catch (err) {
      console.error('Failed to remove notification:', err);
    }
  },

  /* ───────────── Clear all notifications ───────────── */

  clearAllNotifications: async (userId, extensionName) => {
    const colRef = collection(
      db,
      'ulgebraUsers',
      userId,
      'extensions',
      extensionName,
      'notifications'
    );
    // The panel only loads the latest 20, so page through the server copy
    // and delete in batches (a batch holds at most 500 writes). The round
    // cap stops a runaway loop if a delete keeps failing silently.
    for (let round = 0; round < 50; round++) {
      const snap = await getDocsFromServer(query(colRef, limit(400)));
      if (snap.empty) break;
      const batch = writeBatch(db);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      if (snap.docs.length < 400) break;
    }

    set({ notifications: [] });

    // Nothing is left, so nothing is unread.
    const countRef = ref(rtdb, `user_unread_notifications_count/${userId}/${extensionName}`);
    await rtdbSet(countRef, 0);
  },
}));
